import React, { useMemo, useState } from 'react';
import { Pressable, Platform, StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import FileSourceDropdown from '@/components/Dropdowns/FileSourceDropdown';
import {
  formatFileUploadLimits,
  getFileValidationLimits,
} from '@/constants/utils/file_upload_limits';

const createStyles = (colors) => StyleSheet.create({
  container: {
    paddingTop: 4,
    paddingBottom: 4,
  },
  description: {
    color: colors.text_color_secondary,
    fontSize: 12,
    paddingHorizontal: 12,
    paddingVertical: 6,
    lineHeight: 16,
  },
  rowButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    minHeight: 30,
    paddingVertical: 5,
    paddingHorizontal: 12,
    borderRadius: 4,
    marginVertical: 0,
    width: '100%',
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
    }),
  },
  rowButtonHovered: {
    backgroundColor: colors.solid_color_tertiary,
  },
  rowButtonText: {
    color: colors.text_color_primary,
    fontSize: 14,
  },
});

/**
 * Renders the file-upload affordance inside the chat-input parameter dropdown.
 * Shows the parameter description (if any) above a "Choose file" trigger that
 * opens {@link FileSourceDropdown}, giving the user the choice between picking
 * from their library and uploading a fresh file.
 */
const FileUploadControl = ({ param }) => {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [isHovered, setIsHovered] = useState(false);

  const description = param?.ui?.description || param?.description;
  const displayName = param?.ui?.displayName || param?.name || 'file';
  const limitsHint = formatFileUploadLimits(getFileValidationLimits(param?.validation?.file));

  return (
    <View style={styles.container}>
      {!!description && (
        <Text style={styles.description}>{description}</Text>
      )}
      {!!limitsHint && (
        <Text style={styles.description}>{limitsHint}</Text>
      )}
      <FileSourceDropdown param={param} align="start" sideOffset={2}>
        <Pressable
          style={[styles.rowButton, isHovered && styles.rowButtonHovered]}
          onHoverIn={() => setIsHovered(true)}
          onHoverOut={() => setIsHovered(false)}
        >
          <Text style={styles.rowButtonText}>{`Choose ${displayName}`}</Text>
          <Icon name="chevron-down" size={12} color={colors.text_color_primary} />
        </Pressable>
      </FileSourceDropdown>
    </View>
  );
};

export default FileUploadControl;
